/**
 * Playlist Entity
 *
 * A pure domain entity representing a collection of tracks.
 * Contains business rules and invariants - no framework or persistence dependencies.
 */

import { Track } from './Track';

export interface Playlist {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly tracks: readonly Track[];
  readonly coverUrl?: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

/**
 * Get the number of tracks in a playlist.
 */
export function getTrackCount(playlist: Playlist): number {
  return playlist.tracks.length;
}

/**
 * Check if a playlist has no tracks.
 */
export function isEmpty(playlist: Playlist): boolean {
  return playlist.tracks.length === 0;
}

/**
 * Find a track by its ID within a playlist.
 * Returns undefined if not found.
 */
export function getTrackById(playlist: Playlist, id: string): Track | undefined {
  return playlist.tracks.find((track) => track.id === id);
}

/**
 * Find a track by its index within a playlist.
 * Returns undefined if index is out of bounds.
 */
export function getTrackByIndex(playlist: Playlist, index: number): Track | undefined {
  if (index < 0 || index >= playlist.tracks.length) {
    return undefined;
  }
  return playlist.tracks[index];
}

/**
 * Get the index of a track by its ID.
 * Returns -1 if not found.
 */
export function getTrackIndex(playlist: Playlist, id: string): number {
  return playlist.tracks.findIndex((track) => track.id === id);
}

/**
 * Get the total duration of all tracks in the playlist.
 * Only counts tracks that have a duration defined.
 */
export function getTotalDuration(playlist: Playlist): number {
  return playlist.tracks.reduce((total, track) => {
    return total + (track.duration ?? 0);
  }, 0);
}

/**
 * Check if a playlist contains a track with the given ID.
 */
export function containsTrack(playlist: Playlist, trackId: string): boolean {
  return playlist.tracks.some((track) => track.id === trackId);
}

/**
 * Create a new Playlist entity.
 * Throws if required fields are missing.
 */
export function createPlaylist(input: {
  id: string;
  name: string;
  description?: string;
  tracks: Track[];
  coverUrl?: string;
  createdAt?: Date;
  updatedAt?: Date;
}): Playlist {
  if (!input.id || !input.name) {
    throw new Error('Playlist is missing required fields');
  }

  const now = new Date();

  return {
    id: input.id,
    name: input.name,
    description: input.description,
    tracks: Object.freeze([...input.tracks]),
    coverUrl: input.coverUrl,
    createdAt: input.createdAt ?? now,
    updatedAt: input.updatedAt ?? now,
  };
}
